import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import Grid from "../../Components/Grid/Grid";
import Processing from "../../Components/Processing/Processing";
import SchemeBar from "../../Components/SchemeBar/SchemeBar";
import { STATE_RULE, SRS_CELLS, buildConfig, stateClassOf, stateLabelOf, scopeLabel, Severity } from "../../Functions/htScenarios";
import {
    GRID_COLS, GRID_ROWS, IMG, CellSwitches, Maneuvers, emptyManeuvers, buildSRSLayout,
    makeReadName, ownerCellOf, wantBarSepClosed, wantGenOn,
} from "../../Functions/srsScheme";
import { installSRSClicks, installSRSFit, showSRSModal, SwitchInfo } from "../../Functions/srsInteract";

// Optional training-session context. When present the scheme loads in the
// scenario's start state, shows the target banner, logs maneuvers/warnings and
// exposes a Finish button. When absent the page is the plain sandbox.
export interface HTSession {
    teacherId: string;
    studentId: string;
    sessionId: string;
    initialState: string;  // state id (see htScenarios.STATE_OPTIONS)
    finalState: string;    // state id
    cell: string;          // cell id / semistation / "*" (whole station)
    onFinish?: () => void; // navigate to leaderboard after finishing
}

/**
 * "Antrenare" (Training) module: the SRS double-busbar station on the grid
 * (see Functions/srsScheme.ts for the layout and the interlock tables).
 *
 * The trainee brings the selected scope from an initial to a final operative
 * state; every maneuver and every refused (interlocked) maneuver is logged and
 * scored server-side.
 */
const HighTensionTraining = ({ data, useStore, session } : { data: any, useStore: any, session?: HTSession }) => {

    const { schemeNumber } = useParams();
    const navigate = useNavigate();
    const addSign = useStore((state: any) => state.addSign);
    const reset = useStore((state: any) => state.reset);
    const changeGrid = useStore((state: any) => state.changeGrid);
    const imageLocking = useStore((state: any) => state.imageLocking);
    const swapSignImage = useStore((state: any) => state.swapSignImage);
    const [finished, setFinished] = useState<boolean>(false);
    // Finish report shown after the trainee ends the session (null = not finished).
    const [report, setReport] = useState<{ score: number | null, findings: any, missing?: string[] } | null>(null);

    const cellMeta = useRef<Record<string, CellSwitches>>({});
    const maneuvers = useRef<Maneuvers>(emptyManeuvers());
    const logRef = useRef<any[]>([]);              // recorded maneuver/warning events
    const sessionRef = useRef<HTSession | undefined>(session);
    const selectedCellsRef = useRef<string[]>([]); // cell ids covered by the selection
    useEffect(() => { sessionRef.current = session; }, [session]);

    // ---- helpers shared by init, click-logging and finish ------------------
    const readName = makeReadName(useStore);
    const isCellClosed = (cell?: [number, number]): boolean => !!cell && readName(cell[0], cell[1]).includes("inchis");
    const isGenOn = (cell?: [number, number]): boolean => !!cell && readName(cell[0], cell[1]).includes("pornit");
    const ownerCell = (x: number, y: number) => ownerCellOf(cellMeta.current, x, y);
    const setImg = (cell: [number, number] | undefined, img: any) => { if (cell) swapSignImage(cell[0], cell[1], 1, img); };

    // Set one cell's switches to a state rule (used to init the start state).
    const setCellState = (id: string, rule: typeof STATE_RULE[keyof typeof STATE_RULE]) => {
        const m = cellMeta.current[id];
        if (!m) return;
        // On a "closed" bar-sep state only the primary bar is closed (a bay sits
        // on one bar); "open" opens every bar sep. A coupler closes BOTH bars.
        m.barSeps.forEach((bs) => setImg(bs.cell, wantBarSepClosed(id, bs.bar, rule) ? IMG.sepClosed : IMG.sepOpen));
        setImg(m.lineSep, rule.lineSep === "closed" ? IMG.sepClosed : IMG.sepOpen);
        setImg(m.breaker, rule.breaker === "closed" ? IMG.intrClosed : IMG.intrOpen);
        setImg(m.clp, rule.clp === "closed" ? IMG.clpClosed : IMG.clpOpen);
        if (m.generator) setImg(m.generator, wantGenOn(rule) ? IMG.genOn : IMG.genOff);
    };

    // Does a cell currently match a state rule (final-state check)?
    const cellMatchesState = (id: string, rule: typeof STATE_RULE[keyof typeof STATE_RULE]): boolean => {
        const m = cellMeta.current[id];
        if (!m) return true;
        for (const bs of m.barSeps) {
            if (isCellClosed(bs.cell) !== wantBarSepClosed(id, bs.bar, rule)) return false;
        }
        if (m.lineSep && isCellClosed(m.lineSep) !== (rule.lineSep === "closed")) return false;
        if (m.breaker && isCellClosed(m.breaker) !== (rule.breaker === "closed")) return false;
        if (m.clp && isCellClosed(m.clp) !== (rule.clp === "closed")) return false;
        // Generator: running only in normal operation (energized + breaker closed).
        if (m.generator && isGenOn(m.generator) !== wantGenOn(rule)) return false;
        return true;
    };
    const computeFinalMatch = (): boolean | null => {
        const s = sessionRef.current;
        if (!s) return null;
        if (s.finalState === "free") return null; // free practice -> not scored
        // Only the exercise's own cells must match the final state; maneuvers on
        // unrelated cells cost a wasteful-maneuver penalty, not a "not reached".
        const cfg = buildConfig(s.initialState, s.finalState, s.cell);
        return selectedCellsRef.current.every((id) => cellMatchesState(id, STATE_RULE[cfg.target[id]]));
    };

    // Human-readable list of what is still wrong vs the target — shown when the
    // requested end-state was not reached (report + leaderboard).
    const computeMissing = (): string[] => {
        const s = sessionRef.current;
        if (!s) return [];
        const cfg = buildConfig(s.initialState, s.finalState, s.cell);
        const M = maneuvers.current;
        const out: string[] = [];
        SRS_CELLS.filter((c) => selectedCellsRef.current.includes(c.id)).forEach((c) => {
            const m = cellMeta.current[c.id];
            if (!m) return;
            const rule = STATE_RULE[cfg.target[c.id]];
            const label = c.label.replace(/\s*\(.*\)$/, "");
            for (const bs of m.barSeps) {
                const want = wantBarSepClosed(c.id, bs.bar, rule);
                if (isCellClosed(bs.cell) !== want) {
                    const bus = M.sepBarLine[`${bs.cell[0]}_${bs.cell[1]}`];
                    out.push(`${label}: separatorul de bară${bus ? " " + bus : ""} trebuie ${want ? "închis" : "deschis"}`);
                }
            }
            if (m.lineSep && isCellClosed(m.lineSep) !== (rule.lineSep === "closed"))
                out.push(`${label}: separatorul de linie trebuie ${rule.lineSep === "closed" ? "închis" : "deschis"}`);
            if (m.breaker && isCellClosed(m.breaker) !== (rule.breaker === "closed"))
                out.push(`${label}: întreruptorul trebuie ${rule.breaker === "closed" ? "închis" : "deschis"}`);
            if (m.clp && isCellClosed(m.clp) !== (rule.clp === "closed"))
                out.push(`${label}: CLP-ul trebuie ${rule.clp === "closed" ? "închis (legat la pământ)" : "deschis"}`);
            if (m.generator) {
                const on = wantGenOn(rule);
                if (isGenOn(m.generator) !== on) out.push(`${label}: generatorul trebuie ${on ? "pornit" : "oprit"}`);
            }
        });
        return out;
    };

    // Total switch operations the exercise needs (initial -> target for the
    // selected cells). Drives the completion-based score.
    const computeRequired = (): number => {
        const s = sessionRef.current;
        if (!s || s.finalState === "free") return 0;
        const cfg = buildConfig(s.initialState, s.finalState, s.cell);
        let n = 0;
        selectedCellsRef.current.forEach((id) => {
            const m = cellMeta.current[id];
            if (!m) return;
            const start = STATE_RULE[cfg.start[id]], target = STATE_RULE[cfg.target[id]];
            m.barSeps.forEach((bs) => {
                if (wantBarSepClosed(id, bs.bar, start) !== wantBarSepClosed(id, bs.bar, target)) n++;
            });
            if (m.lineSep && (start.lineSep === "closed") !== (target.lineSep === "closed")) n++;
            if (m.breaker && (start.breaker === "closed") !== (target.breaker === "closed")) n++;
            if (m.clp && (start.clp === "closed") !== (target.clp === "closed")) n++;
            if (m.generator && wantGenOn(start) !== wantGenOn(target)) n++;
        });
        return n;
    };

    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    // Persist the running log to the session (score computed server-side).
    const autosave = (finalStateMatches: boolean | null = null, missing: string[] = []) => {
        const s = sessionRef.current;
        if (!s || !BACKEND) return Promise.resolve();
        return fetch(`${BACKEND}/api/session-httraining/edit-session-student-httraining`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ teacherId: s.teacherId, studentId: s.studentId, sessionId: s.sessionId, currLogs: logRef.current, finalStateMatches, missing, required: computeRequired() }),
        }).then((r) => r.json()).catch(() => null);
    };
    // Record a maneuver (successful toggle) or an interlock warning.
    const logEvent = (kind: "maneuver" | "warning", severity: Severity | null, category: string, msg: string, x: number, y: number) => {
        if (!sessionRef.current) return;
        const type = kind === "maneuver" ? "correct" : severity === "critical" || severity === "major" ? "wrong" : "warning";
        logRef.current.push({ kind, severity, category, msg, cell: ownerCell(x, y), type, ts: Date.now() });
        autosave();
    };

    const finishSession = async () => {
        const s = sessionRef.current;
        if (!s) return;
        const finalStateMatches = computeFinalMatch();
        const missing = finalStateMatches === false ? computeMissing() : [];
        const res = await autosave(finalStateMatches, missing);
        if (BACKEND) {
            await fetch(`${BACKEND}/api/session-httraining/finish-session-student-httraining`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ teacherId: s.teacherId, studentId: s.studentId, sessionId: s.sessionId }),
            }).catch(() => null);
        }
        setReport({ score: res?.score ?? null, findings: res?.findings ?? null, missing });
    };

    // Auto-fit the custom canvas once the scheme is painted.
    useEffect(() => {
        if (!finished) return;
        return installSRSFit(GRID_COLS, GRID_ROWS);
    }, [finished]);

    // Log a successful toggle + flag it if it acts on a cell outside the
    // exercise's selected cell(s).
    const logManeuver = (info: SwitchInfo) => {
        const { x, y, what, act } = info;
        logEvent("maneuver", null, "maneuver", `${act} ${what}`, x, y);
        const owner = ownerCell(x, y);
        // Wasteful maneuver = a switch that, AFTER the toggle, is NOT at its
        // target position. This fires only on the move that goes the wrong way
        // (e.g. close CLP ok, open CLP -> warn, close CLP again -> ok). Free
        // practice never nags / penalizes.
        if (sessionRef.current && sessionRef.current.finalState !== "free" && owner) {
            const sc = sessionRef.current;
            const cfg = buildConfig(sc.initialState, sc.finalState, sc.cell);
            const rule = STATE_RULE[cfg.target[owner]];
            const m = cellMeta.current[owner];
            // Target position of the specific switch that was operated.
            let wantClosed: boolean | null = null;
            if (m) {
                const bs = m.barSeps.find((b) => b.cell[0] === x && b.cell[1] === y);
                if (bs) wantClosed = wantBarSepClosed(owner, bs.bar, rule);
                else if (m.lineSep && m.lineSep[0] === x && m.lineSep[1] === y) wantClosed = rule.lineSep === "closed";
                else if (m.breaker && m.breaker[0] === x && m.breaker[1] === y) wantClosed = rule.breaker === "closed";
                else if (m.clp && m.clp[0] === x && m.clp[1] === y) wantClosed = rule.clp === "closed";
                else if (m.generator && m.generator[0] === x && m.generator[1] === y) wantClosed = wantGenOn(rule);
            }
            const isOn = info.kind === "generator" ? isGenOn([x, y]) : isCellClosed([x, y]);
            if (wantClosed !== null && isOn !== wantClosed) {
                // Logged (counts on the results + leaderboard + score) but NOT
                // shown as a live modal during the session.
                if (selectedCellsRef.current.includes(owner))
                    logEvent("warning", "minor", "reverse", `Manevră ${act.toLowerCase()} ${what} aduce celula ${owner} într-o stare greșită (nu spre starea cerută) — manevră inutilă.`, x, y);
                else
                    logEvent("warning", "minor", "unnecessary", `Manevră inutilă: celula ${owner} nu face parte din exercițiul curent.`, x, y);
            }
        }
        // Multi-scope de-energizing (bus / semistation / whole station): the
        // consumers must be fully maneuvered before touching the generators.
        if (sessionRef.current && (owner === "GENERATOR 1" || owner === "GENERATOR 2")) {
            const sc = sessionRef.current;
            const cfg = buildConfig(sc.initialState, sc.finalState, sc.cell);
            const multi = selectedCellsRef.current.length > 1;
            // De-energizing = leaving normal operation toward another state.
            const deEnergizing = stateClassOf(sc.initialState) === "NORMAL" && stateClassOf(sc.finalState) !== "NORMAL";
            if (multi && deEnergizing) {
                const consumersDone = selectedCellsRef.current
                    .filter((id) => id !== "GENERATOR 1" && id !== "GENERATOR 2")
                    .every((id) => cellMatchesState(id, STATE_RULE[cfg.target[id]]));
                if (!consumersDone) {
                    const msg = "Trebuie să faci întâi manevrele pentru consumatori, apoi pentru generatoare!";
                    logEvent("warning", "major", "generator-order", msg, x, y);
                    showSRSModal(msg);
                }
            }
        }
    };

    // Make the switches actionable: click toggles state (after the interlocks).
    useEffect(() => installSRSClicks({
        useStore,
        swapSignImage,
        maneuvers: () => maneuvers.current,
        onManeuver: logManeuver,
        onWarning: (severity, category, msg, x, y) => logEvent("warning", severity, category, msg, x, y),
    }), []);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        reset();
        changeGrid();
        imageLocking();

        const layout = buildSRSLayout(addSign);
        cellMeta.current = layout.cellMeta;
        maneuvers.current = layout.maneuvers;

        // Initialize the chosen initial state (training session only): the whole
        // station starts in normal operation; only the selected scope begins in
        // the chosen initial state.
        logRef.current = [];
        if (session) {
            // Free practice: no target -> start every cell in its normal state.
            const finalForInit = session.finalState === "free" ? session.initialState : session.finalState;
            const cfg = buildConfig(session.initialState, finalForInit, session.cell);
            // Cells actually involved = those whose start/target differs from their
            // normal state (covers busbar earthing via a C.M. and the CTV rule).
            selectedCellsRef.current = session.finalState === "free" ? [] : SRS_CELLS.filter((c) => {
                const n = c.normalState || "NORMAL";
                return cfg.start[c.id] !== n || cfg.target[c.id] !== n;
            }).map((c) => c.id);
            // Every cell (incl. the longitudinal couplings) is set from the config.
            SRS_CELLS.forEach((c) => setCellState(c.id, STATE_RULE[cfg.start[c.id]]));
        } else {
            selectedCellsRef.current = [];
        }

        // Show current colouring immediately and switch on the HT look.
        document.body.classList.add("show-current");
        document.body.classList.add("ht-scheme");
        setFinished(true);

        return () => {
            document.body.classList.remove("show-current");
            document.body.classList.remove("ht-scheme");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schemeNumber]);

    const cellLabel = session ? scopeLabel(session.cell) : "";

    if (!finished) return <Processing />;

    return (
        <>
            <SchemeBar
                data={data}
                title="Antrenare — stația SRS"
                subtitle={cellLabel}
                onBack={() => navigate(-1)}
                hideBack
            >
                {/* The objective rides inside the bar rather than as a second
                    banner under it: on a scheme page every pixel of chrome is
                    taken off the drawing. */}
                {session ? (
                    <div className="ht-scenario-banner">
                        {session.finalState === "free" ? (
                            <span className="ht-scenario-sub">Antrenament liber — exersează manevrele pe toată stația (fără punctaj).</span>
                        ) : (
                            <span className="ht-scenario-sub">Adu <b>{cellLabel}</b> din <b>{stateLabelOf(session.initialState)}</b> in <b>{stateLabelOf(session.finalState)}</b>.</span>
                        )}
                    </div>
                ) : null}
            </SchemeBar>

            <div className="ht-back-btn" onClick={() => navigate(-1)} title="Înapoi">
                <FontAwesomeIcon icon={faChevronLeft} className="back-icon" />
            </div>

            <Grid
                printSchemes={null}
                removePage={() => {}}
                totalPages={1}
                category="0"
                isSimulation={true}
                debugSchemeNumber={-1}
                page={1}
                useStore={useStore}
                data={data}
                projectId={null}
                format="A3"
                gridColumns={GRID_COLS}
                gridRows={GRID_ROWS}
                projectName="SRS - Antrenare"
                teacherVar={true}
            />

            {session && !report && (
                <button type="button" className="ht-finish-btn" onClick={finishSession}>
                    Finalizeaza sesiunea
                </button>
            )}

            {report && (
                <div className="ht-modal-overlay">
                    <div className="ht-modal ht-report">
                        <h2>Rezultatul sesiunii</h2>
                        <p className="ht-report-score">
                            {report.score === null ? "Antrenament liber (fără punctaj)" : `Scor: ${report.score} / 100`}
                        </p>
                        {report.findings && (["critical", "major", "minor"] as const).map((sev) => {
                            const items = report.findings[sev] || [];
                            if (!items.length) return null;
                            const title = sev === "critical" ? "Erori grave" : sev === "major" ? "Greșeli" : "Avertismente";
                            return (
                                <div key={sev} className={`ht-report-group ht-${sev}`}>
                                    <h3>{title} ({items.length})</h3>
                                    <ul>{items.map((it: any, i: number) => <li key={i}>{it.msg}</li>)}</ul>
                                </div>
                            );
                        })}
                        {report.missing && report.missing.length > 0 && (
                            <div className="ht-report-group ht-major">
                                <h3>Nu ai atins starea ceruta ({report.missing.length})</h3>
                                <ul>{report.missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
                            </div>
                        )}
                        {report.findings && !["critical", "major", "minor"].some((s) => (report.findings[s] || []).length) && (!report.missing || !report.missing.length) && (
                            <p>Nicio greseala inregistrata. Felicitari!</p>
                        )}
                        <div className="ht-modal-buttons">
                            <button type="button" className="ok" onClick={() => session?.onFinish?.()}>Vezi clasamentul</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HighTensionTraining;
